import { AuthenticationResponse } from "@/api/model/authenticationResponse"
import {
  AXLE_COMPONENT_TOKEN_COOKIE_NAME,
  AXLE_COMPONENT_TOKEN_COOKIE_OPTIONS,
  SMART_CHARGING_COMPONENT_TOKEN_COOKIE_NAME,
} from "@/constants/axle"
import { getApiRootDomain } from "@/lib/shared/getRootDomain"
import { getCookie, removeCookie, setCookie } from "@/lib/shared/useCookies.ts"
import ErrorReporter from "@/services/ErrorReporter"
import { useNow } from "@vueuse/core"
import { computed, ref } from "vue"

const AuthCookie = {
  longLivedSession: "myaccount_remember-me_long_lived_refresh_token",
  refreshTokenAvailable: "website_refresh_token_available",
}

const cookieOptions = {
  path: "/",
  domain: getApiRootDomain(),
}

const accessToken = ref<string | null>(null)
const refreshTokenExpiry = ref<Date | null>(null)
const accessTokenExpiry = ref<Date | null>(null)
let hasStaleRefreshCookie = false
const sessionClearedListeners = new Set<() => void>()

/** Runs `listener` whenever the session is cleared: log out, a failed refresh, the auto-login guard. */
export function onSessionCleared(listener: () => void): () => void {
  sessionClearedListeners.add(listener)
  return () => sessionClearedListeners.delete(listener)
}

function hasRefreshTokenCookie() {
  return !!getCookie(AuthCookie.refreshTokenAvailable)
}

function isLongLived() {
  return getCookie(AuthCookie.longLivedSession) === true
}

export function useAuthSession() {
  const now = useNow()

  const isExpired = computed(() => {
    return (
      refreshTokenExpiry.value && now.value > new Date(refreshTokenExpiry.value)
    )
  })

  const isAccessTokenValid = () =>
    Boolean(
      accessTokenExpiry.value &&
      new Date() <= new Date(accessTokenExpiry.value),
    )
  const isAuthenticated = computed(() => isAccessTokenValid())

  function hasValidRefreshTokenCookie() {
    return hasRefreshTokenCookie() && !hasStaleRefreshCookie
  }

  function clearSessionData() {
    accessToken.value = null
    refreshTokenExpiry.value = null
    accessTokenExpiry.value = null

    removeCookie(AuthCookie.longLivedSession, cookieOptions)
    removeCookie(AuthCookie.refreshTokenAvailable, cookieOptions)

    removeCookie(
      AXLE_COMPONENT_TOKEN_COOKIE_NAME,
      AXLE_COMPONENT_TOKEN_COOKIE_OPTIONS,
    )

    removeCookie(
      SMART_CHARGING_COMPONENT_TOKEN_COOKIE_NAME,
      AXLE_COMPONENT_TOKEN_COOKIE_OPTIONS,
    )

    sessionClearedListeners.forEach((listener) => {
      try {
        listener()
      } catch (error) {
        // A listener must never stop the session from clearing; it is reported, not rethrown.
        ErrorReporter.report(error)
      }
    })

    if (hasRefreshTokenCookie()) {
      // When we fail to refresh the token, the cookie auth cookie should be removed
      // if for any reason it isn't removed, we need to indicate it cannot be used, so
      // we don't try to use it again until the user explicitly logs in again
      hasStaleRefreshCookie = true
    }
  }

  function setSessionData(
    data: AuthenticationResponse,
    isSessionExtended: boolean,
  ) {
    hasStaleRefreshCookie = false
    accessToken.value = data.accessToken
    accessTokenExpiry.value = new Date(data.accessTokenExpiry)
    refreshTokenExpiry.value = new Date(data.sessionExpiry)

    if (isSessionExtended) {
      setCookie(AuthCookie.longLivedSession, true, {
        ...cookieOptions,
        expires: refreshTokenExpiry.value,
      })
    }
    setCookie(AuthCookie.refreshTokenAvailable, true, {
      ...cookieOptions,
      expires: refreshTokenExpiry.value,
    })
  }

  return {
    accessToken,
    isAuthenticated,
    accessTokenExpiry,
    sessionExpiry: refreshTokenExpiry,
    isExpired,
    hasRefreshTokenCookie,
    hasValidRefreshTokenCookie,
    isLongLived,
    isAccessTokenValid,
    clearSessionData,
    setSessionData,
  }
}
